import {
  AccountData,
  FormInputType,
  NotificationType,
  SavedFormQuestion,
  SavedFormQuestionState,
} from "@markit/common.types";
import { Colors } from "../../../utils/colors";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import ActiveInactiveLabel from "../ActiveInactiveLabel";
import ButtonWithDropdown from "../../Buttons/ButtonWithDropdown";
import { memo, useCallback, useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useTheme";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import { Icon } from "@iconify/react";
import {
  formQuestionDisplayName,
  handlePluralString,
} from "@markit/common.utils";
import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import LargePopupModalHeader from "../../Containers/LargePopupModalHeader";
import SelectRecipientsCollectedData from "../../CreatorDashboard/MassTexts/SelectRecipientsFlow/SelectCollectedData/SelectRecipientsCollectedData";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import ThreeDotsButton from "../../Buttons/ThreeDotsButton";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import {
  deleteSavedQuestion,
  getAccountState,
  updateSavedQuestion,
} from "../../../redux/slices/accountSlice";
import FullProfilePreviewModal from "../../FollowerProfile/FullProfilePreviewModal";

export const DATA_QUESTION_FLEX_SECTIONS = [1, 1, 2];

type DataQuestionItemProps = {
  item: SavedFormQuestion;
  numResponses: number;
  editOnPress: () => void;
};

const DataQuestionItem = ({
  item,
  editOnPress,
  numResponses,
}: DataQuestionItemProps) => {
  const { theme } = useTheme();
  const { uid } = useSelector(getAccountState).account.accountData;
  const dispatch = useDispatch();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileSelected, setProfileSelected] = useState<AccountData>();

  const styles = {
    itemBorder: { border: `1px solid ${theme.SubText.color}` },
  };

  const isActive = useMemo(() => {
    return item.state === SavedFormQuestionState.ACTIVE;
  }, [item.state]);

  const numTimesUsed = useMemo(() => {
    return item.onProfileFollow
      ? item.eventIds.length + 1
      : item.eventIds.length;
  }, [item.eventIds, item.onProfileFollow]);

  const copyQuestionId = useCallback(() => {
    navigator.clipboard.writeText(item.id).then(() => {
      showNotificationBanner(dispatch, "Copied!", NotificationType.AFFIRMATIVE);
    });
  }, [dispatch, item.id]);

  const deleteQuestionOnPress = useCallback(() => {
    dispatch(deleteSavedQuestion(uid, item.id, numResponses));
    setShowDropdown(false);
  }, [dispatch, item.id, numResponses, uid]);

  const updateQuestionStateOnPress = useCallback(() => {
    if (item.state === SavedFormQuestionState.ACTIVE) {
      setShowDeleteModal(true);
    } else {
      dispatch(
        updateSavedQuestion(uid, {
          ...item,
          state: SavedFormQuestionState.ACTIVE,
        })
      );
      showNotificationBanner(
        dispatch,
        "Re-activated!",
        NotificationType.AFFIRMATIVE
      );
    }
    setShowDropdown(false);
  }, [dispatch, item, uid]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems: DropdownMenuItem[] = [
      {
        title: "Edit",
        icon: "ion:create",
        key: "edit",
        onPress: () => {
          editOnPress();
          setShowDropdown(false);
        },
        isDestructive: false,
      },
    ];

    if (!isVisible) {
      menuItems.push({
        title: "View Collected Data",
        icon: "ion:arrow-forward",
        key: "view",
        onPress: () => {
          setIsVisible(true);
          setShowDropdown(false);
        },
        isDestructive: false,
      });
    }
    menuItems.push(
      {
        title: "Copy Question ID",
        subtext: <span className="smallBodySubtext">{item.id}</span>,
        icon: "ion:copy",
        key: "copy_question_id",
        onPress: () => {
          copyQuestionId();
          setShowDropdown(false);
        },
        isDestructive: false,
      },
      {
        title:
          item.state === SavedFormQuestionState.ACTIVE ? "Delete" : "Activate",
        icon:
          item.state === SavedFormQuestionState.ACTIVE
            ? "ion:trash"
            : "ion:checkmark-circle",
        key: "delete",
        onPress: updateQuestionStateOnPress,
        isDestructive: item.state === SavedFormQuestionState.ACTIVE,
      }
    );

    return menuItems;
  }, [
    copyQuestionId,
    editOnPress,
    isVisible,
    item.id,
    item.state,
    updateQuestionStateOnPress,
  ]);

  return (
    <div>
      <div
        className="AlignedRowSelect"
        style={{ paddingBlock: 10 }}
        onClick={() => setIsVisible(true)}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 7, flex: DATA_QUESTION_FLEX_SECTIONS[0] }}
        >
          <div className="AlignedRow" style={{ gap: 7, maxWidth: 280 }}>
            {item.isEssential ? (
              <div>
                <Icon
                  icon="fa:map-pin"
                  height={14}
                  style={{ color: theme?.PrimaryText.color }}
                />
              </div>
            ) : null}
            <span className="bodyMedium TextOverflowEllipsis">
              {item.formInput.label}
            </span>
          </div>
          <div className="AlignedRowSelect" style={{ gap: 7 }}>
            <Icon
              icon={
                item.formInput.inputType === FormInputType.RADIO
                  ? "mdi:circle-multiple"
                  : item.formInput.inputType === FormInputType.CHECK_BOX
                    ? "mdi:checkbox-multiple-marked"
                    : item.formInput.inputType === FormInputType.TEXT_INPUT
                      ? "mdi:text-box"
                      : item.formInput.inputType === FormInputType.FILE_UPLOAD
                        ? "ion:document"
                        : "ion:mail"
              }
              height={14}
              style={{ color: Colors.GRAY1 }}
            />
            <span className="smallBodySubtext">
              {formQuestionDisplayName(item.formInput)}
            </span>
          </div>
          <span className="smallBodySubtext">
            {numTimesUsed} {handlePluralString("Use", numTimesUsed)}
          </span>
        </div>
        <div
          className="ColumnNormal"
          style={{ flex: DATA_QUESTION_FLEX_SECTIONS[1], gap: 7 }}
        >
          <span className="bodyMedium">
            {numResponses} {handlePluralString("Response", numResponses)}
          </span>
          <div className="AlignedRow" style={{ gap: 2 }}>
            <span className="smallBodySubtext">Detailed Breakdown</span>
            <Icon
              icon="ion:chevron-forward"
              height={12}
              style={{ color: Colors.GRAY1, marginBottom: -1 }}
            />
          </div>
        </div>
        <div
          className="AlignedRowSpaced"
          style={{ flex: DATA_QUESTION_FLEX_SECTIONS[2], cursor: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <ActiveInactiveLabel isActive={isActive} />
          <ButtonWithDropdown
            buttonLabel="View"
            textColor={Colors.BLACK}
            isOpenDropdown={showDropdown}
            setIsOpenDropdown={setShowDropdown}
            onPress={() => setIsVisible(true)}
            dropdownItems={dropdownItems}
            containerStyles={styles.itemBorder}
          />
        </div>
      </div>
      <HorizontalDivider altMargin={14} />
      {isVisible ? (
        <LargePopupModalContainer
          showModal={isVisible}
          headerComp={
            <LargePopupModalHeader
              backOnPress={() => setIsVisible(false)}
              headerRight={
                <DropdownMenu
                  dropdownItems={dropdownItems}
                  trigger={["click"]}
                  placement="bottomLeft"
                  altWidth={200}
                >
                  <ThreeDotsButton onPress={() => {}} />
                </DropdownMenu>
              }
            />
          }
          containerStyles={{ height: "90%" }}
          valueComp={
            <div className="ColumnNormal PreventScroll">
              <SelectRecipientsCollectedData
                unsavedRecipients={[]}
                setUnsavedRecipients={() => {}}
                unsavedCategories={[]}
                setUnsavedCategories={() => {}}
                showCategories={false}
                excludingMode={false}
                setProfileSelected={setProfileSelected}
                selectedQuestion={item}
                selectable={false}
              />
              {profileSelected ? (
                <FullProfilePreviewModal
                  profileSelected={profileSelected}
                  setProfileSelected={setProfileSelected}
                />
              ) : null}
            </div>
          }
        />
      ) : null}
      <ConfirmDeleteModal
        heading={"Delete Data Question?"}
        subtext={
          numTimesUsed > 0
            ? "This data question will be marked as inactive. You will still be able to select and segment by data collected using this question."
            : "Are you sure you want to delete this data question? This cannot be undone."
        }
        deleteButtonText="Delete Question"
        hideModal={!showDeleteModal}
        setIsVisible={setShowDeleteModal}
        deleteOnPress={deleteQuestionOnPress}
      />
    </div>
  );
};

export default memo(DataQuestionItem);
